import React from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Users,
  FileText,
  Settings,
  Shield,
  Database,
  Trash2,
  Download,
  UserCheck,
  UserX,
  BarChart3,
  Wrench,
  Mail,
  BookOpen,
  Clock,
  AlertTriangle,
  Globe
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, color = "text-primary" }) => (
  <Card className="mb-6">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-3">
      {children}
    </CardContent>
  </Card>
);

const AdminGuide = () => {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="admin-guide-page">
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">Administrator Guide</h1>
          <p className="text-muted-foreground mt-1">Complete system administration manual for Riverbank Academy Plagiarism Checker</p>
        </div>

        <Section icon={BookOpen} title="System Overview">
          <p>The Riverbank Academy Plagiarism Checker is a web-based academic integrity tool that allows students to submit documents for similarity checking. As an administrator, you have full control over user management, system settings, and data maintenance.</p>
          <div className="bg-muted/50 rounded-sm p-3 mt-2">
            <p><strong>Admin Login:</strong> Use your admin email and password at the login page.</p>
            <p><strong>Admin Dashboard:</strong> Accessible at <strong>/admin</strong> after logging in.</p>
          </div>
        </Section>

        {/* ===== USER MANAGEMENT ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">User Management</h2>
        </div>

        <Section icon={UserCheck} title="Approving New Users" color="text-emerald-600">
          <p>All new registrations (both email and Google Sign-In) require admin approval before students can access the system.</p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>When a new user registers, you will receive an <strong>email notification</strong> at your admin email</li>
            <li>A <strong>pending approval alert</strong> also appears on the Admin Dashboard</li>
            <li>Navigate to <strong>Users</strong> page from the sidebar</li>
            <li>Pending users are highlighted with an amber <strong>"Pending"</strong> badge</li>
            <li>Click <strong>"Approve"</strong> to grant access — the student receives an approval email</li>
            <li>Click <strong>"Reject"</strong> to deny access — the student receives a rejection email and their account is deleted</li>
          </ol>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mt-2">
            <p className="text-emerald-800"><strong>Tip:</strong> Use the "All Users" dropdown filter and select "Pending" to quickly see all users awaiting approval.</p>
          </div>
        </Section>

        <Section icon={Users} title="Managing Existing Users" color="text-blue-600">
          <p>From the <strong>Users</strong> page, you can:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Search</strong> users by name, email, or student ID</li>
            <li><strong>Filter</strong> by role (Student/Admin), status (Active/Inactive), or approval status</li>
            <li><strong>Deactivate/Activate</strong> — temporarily disable a user's access via the action menu (three dots)</li>
            <li><strong>Reset Password</strong> — sends a password reset email to the user</li>
            <li><strong>Delete User</strong> — permanently removes the user and all their submissions</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-2">
            <p className="text-amber-800"><strong>Note:</strong> Deactivating a user preserves their data but blocks login. Deleting a user permanently removes all their data.</p>
          </div>
        </Section>

        {/* ===== DASHBOARD & ANALYTICS ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">Dashboard & Analytics</h2>
        </div>

        <Section icon={BarChart3} title="Admin Dashboard" color="text-purple-600">
          <p>The dashboard provides an overview of system activity:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Total Users</strong> — number of registered users and how many are active</li>
            <li><strong>Total Submissions</strong> — number of documents scanned, with weekly count</li>
            <li><strong>Average Similarity</strong> — the average similarity score across all scans</li>
            <li><strong>Repository Size</strong> — total storage used by the internal document repository</li>
            <li><strong>Status Distribution</strong> — breakdown of submissions by status (completed, processing, error)</li>
            <li><strong>Risk Distribution</strong> — breakdown by risk level (low, moderate, high, critical)</li>
            <li><strong>Recent Submissions</strong> — the 5 most recent submissions with scores</li>
          </ul>
        </Section>

        <Section icon={FileText} title="Submissions Management" color="text-foreground">
          <p>The <strong>Submissions</strong> page shows all student submissions across the system:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>View submission details, similarity scores, and risk levels</li>
            <li>Filter by status (completed, processing, error)</li>
            <li>Click any submission to view the full report</li>
            <li>Download reports in PDF or HTML format</li>
          </ul>
        </Section>

        {/* ===== SYSTEM SETTINGS ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">System Settings</h2>
        </div>

        <Section icon={Settings} title="Configuring Thresholds" color="text-foreground">
          <p>Navigate to <strong>Settings</strong> to configure:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Low Threshold</strong> (default: 15%) — below this is considered low risk</li>
            <li><strong>Moderate Threshold</strong> (default: 30%) — between low and this is moderate risk</li>
            <li><strong>High Threshold</strong> (default: 50%) — above this is critical risk</li>
            <li><strong>Max File Size</strong> — maximum upload size in MB</li>
            <li><strong>Exclude Bibliography</strong> — toggle to exclude bibliography sections from scanning</li>
            <li><strong>Exclude Quotes</strong> — toggle to exclude quoted text</li>
            <li><strong>Exclude Small Matches</strong> — minimum word count threshold for matches</li>
            <li><strong>Internal Repository</strong> — enable/disable checking against previously submitted Riverbank Academy documents</li>
          </ul>
        </Section>

        {/* ===== MAINTENANCE ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">System Maintenance</h2>
        </div>

        <Section icon={Wrench} title="Maintenance Dashboard" color="text-foreground">
          <p>Navigate to <strong>Maintenance</strong> for system health and cleanup tools. The top section shows real-time statistics:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Total Users</strong> — active vs inactive count</li>
            <li><strong>Submissions</strong> — total with error count</li>
            <li><strong>DB Storage</strong> — database storage in MB</li>
            <li><strong>Disk Usage</strong> — uploaded files storage in MB</li>
          </ul>
        </Section>

        <Section icon={Trash2} title="Data Cleanup Operations" color="text-red-600">
          <p>Available cleanup actions:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Purge Failed Submissions</strong> — removes all submissions with error status and their files</li>
            <li><strong>Cleanup Orphaned Data</strong> — removes scan results that no longer belong to any submission</li>
            <li><strong>Bulk Delete by Date</strong> — delete submissions older than a specific date, optionally filtered by status</li>
            <li><strong>Deactivate Inactive Users</strong> — deactivate student accounts with no submissions in X months</li>
            <li><strong>Cleanup Old Files</strong> — remove uploaded files older than X days (database records are preserved)</li>
          </ul>
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 mt-2">
            <p className="text-red-800"><strong>Warning:</strong> All deletion operations are permanent and cannot be reversed. Always export a backup first.</p>
          </div>
        </Section>

        <Section icon={Download} title="Data Export & Backup" color="text-blue-600">
          <p>Export system data for backup purposes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Export All Data</strong> — downloads users, submissions, scan results, and settings as JSON</li>
            <li><strong>Export Users Only</strong> — downloads user records (without passwords)</li>
            <li><strong>Export Submissions Only</strong> — downloads submissions and scan results</li>
          </ul>
          <p className="mt-2"><strong>Recommended:</strong> Export a full backup weekly or before any bulk maintenance operations.</p>
        </Section>

        <Section icon={Clock} title="Automated Retention Policy" color="text-amber-600">
          <p>Configure automatic data lifecycle rules at the bottom of the Maintenance page:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Auto-Purge Old Submissions</strong> — automatically delete submissions after a configurable retention period (e.g., 730 days)</li>
            <li><strong>Auto-Deactivate Inactive Users</strong> — automatically deactivate student accounts with no activity after X months</li>
          </ul>
          <p>Toggle each policy on/off and set the time periods as needed.</p>
        </Section>

        {/* ===== EMAIL & NOTIFICATIONS ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">Email Notifications</h2>
        </div>

        <Section icon={Mail} title="Automated Emails" color="text-foreground">
          <p>The system sends the following automated emails from <strong>noreply@credentiaai.com</strong>:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>New Registration Alert</strong> — sent to admin when a new user registers</li>
            <li><strong>Account Approved</strong> — sent to student when admin approves their registration</li>
            <li><strong>Account Rejected</strong> — sent to student when admin rejects their registration</li>
            <li><strong>Password Reset</strong> — sent when a user requests password reset</li>
          </ul>
        </Section>

        {/* ===== MOODLE INTEGRATION ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">Moodle Integration</h2>
        </div>

        <Section icon={Globe} title="Moodle REST API" color="text-blue-600">
          <p>The system provides REST API endpoints for integration with Moodle LMS. Your IT team can use these to connect the plagiarism checker to your Moodle platform.</p>
          <p>API documentation is available for your IT team to build the Moodle plugin. Contact the development team for the API specification and authentication keys.</p>
        </Section>

        {/* ===== SECURITY ===== */}
        <div className="pt-2">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4">Security & Best Practices</h2>
        </div>

        <Section icon={Shield} title="Security Recommendations" color="text-red-600">
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Change admin password</strong> regularly</li>
            <li><strong>Review pending registrations</strong> promptly to prevent unauthorized access</li>
            <li><strong>Export backups</strong> before performing bulk operations</li>
            <li><strong>Monitor error submissions</strong> — a spike in errors may indicate system issues</li>
            <li><strong>Deactivate accounts</strong> of students who have graduated or left the academy</li>
            <li><strong>Review high-risk submissions</strong> — submissions with critical similarity scores may need academic integrity review</li>
          </ul>
        </Section>

        <Section icon={AlertTriangle} title="Troubleshooting" color="text-amber-600">
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Student can't log in</strong> — check if their account is approved and active on the Users page</li>
            <li><strong>Submission stuck on "processing"</strong> — the scan may have timed out. The student can re-upload the document</li>
            <li><strong>Emails not being received</strong> — check spam/junk folders. Emails are sent from noreply@credentiaai.com</li>
            <li><strong>High number of error submissions</strong> — purge them from the Maintenance page and check if the issue persists</li>
          </ul>
        </Section>
      </div>
    </Layout>
  );
};

export default AdminGuide;
